import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, FlatList, RefreshControl } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import { colors, spacing, borderRadius, Icon, Alert } from '@tipsy/design-system';
import { cardsApi } from '../api';
import type { Transaction } from '../api';
import type { WalletStackParamList } from '../navigation/MainNavigator';

type CardTransactionsRouteProp = RouteProp<WalletStackParamList, 'CardTransactions'>;

const CardTransactionsScreen = () => {
  const route = useRoute<CardTransactionsRouteProp>();
  const { cardId } = route.params;
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [hasMore, setHasMore] = useState(false);

  const fetchTransactions = useCallback(
    async (refresh = false) => {
      try {
        if (refresh) {
          setIsRefreshing(true);
        }

        const response = await cardsApi.getTransactions(cardId, 20);
        setTransactions(response.transactions);
        setHasMore(response.hasMore);
      } catch {
        Alert.alert('Error', 'Failed to load transactions');
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [cardId],
  );

  const loadMore = async () => {
    if (!hasMore || isLoadingMore || transactions.length === 0) return;

    try {
      setIsLoadingMore(true);
      const lastId = transactions[transactions.length - 1].id;
      const response = await cardsApi.getTransactions(cardId, 20, lastId);
      setTransactions((prev) => [...prev, ...response.transactions]);
      setHasMore(response.hasMore);
    } catch {
      // Ignore load more errors
    } finally {
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const formatAmount = (amount: number, currency: string) => {
    const absAmount = Math.abs(amount) / 100;
    const sign = amount < 0 ? '-' : '+';
    const symbol = currency === 'usd' ? '$' : currency === 'gbp' ? '\u00A3' : '\u20AC';
    return `${sign}${symbol}${absAmount.toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getCategoryIcon = (category: string | null | undefined): string => {
    if (!category) return 'credit-card';

    const icons: Record<string, string> = {
      eating_places_restaurants: 'restaurant',
      grocery_stores: 'shopping-cart',
      service_stations: 'local-gas-station',
      automated_fuel_dispensers: 'local-gas-station',
      drug_stores_pharmacies: 'local-pharmacy',
      hotels_motels_resorts: 'hotel',
      airlines: 'flight',
      car_rental: 'directions-car',
      fast_food_restaurants: 'fastfood',
      entertainment: 'movie',
    };

    return icons[category] || 'credit-card';
  };

  const renderTransaction = ({ item }: { item: Transaction }) => (
    <View style={styles.transactionItem}>
      <View style={styles.transactionIcon}>
        <Icon
          name={getCategoryIcon(item.merchantCategory)}
          size={24}
          color={colors.text.secondary}
        />
      </View>
      <View style={styles.transactionDetails}>
        <Text style={styles.transactionMerchant}>{item.merchantName || 'Unknown Merchant'}</Text>
        <Text style={styles.transactionLocation}>
          {item.merchantCity && item.merchantCountry
            ? `${item.merchantCity}, ${item.merchantCountry}`
            : item.merchantCategory || 'Purchase'}
        </Text>
        <Text style={styles.transactionTime}>
          {formatDate(item.createdAt)} at {formatTime(item.createdAt)}
        </Text>
      </View>
      <View style={styles.transactionAmountContainer}>
        <Text
          style={[styles.transactionAmount, item.amount > 0 && styles.transactionAmountPositive]}
        >
          {formatAmount(item.amount, item.currency)}
        </Text>
        <Text style={styles.transactionType}>{item.type === 'refund' ? 'Refund' : 'Purchase'}</Text>
      </View>
    </View>
  );

  const renderFooter = () => {
    if (!isLoadingMore) return null;
    return (
      <View style={styles.loadingMore}>
        <ActivityIndicator size="small" color={colors.interactive.primary} />
      </View>
    );
  };

  const renderEmpty = () => (
    <View style={styles.emptyContainer}>
      <Icon name="credit-card" size={64} color={colors.text.tertiary} style={styles.emptyIcon} />
      <Text style={styles.emptyTitle}>No Transactions Yet</Text>
      <Text style={styles.emptyText}>
        Your card transactions will appear here once you start using your virtual card.
      </Text>
    </View>
  );

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container} edges={['bottom']}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.interactive.primary} />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container} edges={['bottom']}>
      <FlatList
        data={transactions}
        renderItem={renderTransaction}
        keyExtractor={(item) => item.id}
        contentContainerStyle={styles.listContent}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={() => fetchTransactions(true)}
            tintColor={colors.interactive.primary}
          />
        }
        onEndReached={loadMore}
        onEndReachedThreshold={0.5}
        ListFooterComponent={renderFooter}
        ListEmptyComponent={renderEmpty}
        showsVerticalScrollIndicator={false}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  listContent: {
    flexGrow: 1,
    paddingHorizontal: spacing.base,
    paddingTop: spacing.md,
  },
  transactionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface.primary,
    borderRadius: borderRadius.lg,
    padding: spacing.base,
    marginBottom: spacing.md,
  },
  transactionIcon: {
    width: 48,
    height: 48,
    borderRadius: borderRadius.lg,
    backgroundColor: colors.background.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
  },
  transactionDetails: {
    flex: 1,
  },
  transactionMerchant: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text.primary,
  },
  transactionLocation: {
    fontSize: 13,
    color: colors.text.secondary,
    marginTop: spacing.xs,
  },
  transactionTime: {
    fontSize: 12,
    color: colors.text.tertiary,
    marginTop: spacing.xs,
  },
  transactionAmountContainer: {
    alignItems: 'flex-end',
  },
  transactionAmount: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text.primary,
  },
  transactionAmountPositive: {
    color: colors.status.success,
  },
  transactionType: {
    fontSize: 12,
    color: colors.text.tertiary,
    marginTop: spacing.xs,
  },
  loadingMore: {
    paddingVertical: spacing.lg,
    alignItems: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: spacing.xl,
    paddingTop: 100,
  },
  emptyIcon: {
    marginBottom: spacing.lg,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: spacing.sm,
  },
  emptyText: {
    fontSize: 16,
    color: colors.text.secondary,
    textAlign: 'center',
    lineHeight: 24,
  },
});

export default CardTransactionsScreen;
